import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, switchMap, finalize } from 'rxjs';
import { AdminAuthService } from '../auth/admin-auth.service';

/**
 * Attaches `X-Tenant-ID` to outgoing requests, admin app only (#2499).
 *
 * ## Client failure modes (the server independently re-validates tenant
 * membership via `TenantContextGuard` / `@TenantScoped` — see CONTEXT.md —
 * so neither case below is a security gap, only a client UX gap):
 *
 *  - **Key missing** (e.g. `localStorage['tenantId']` cleared, first request
 *    after a storage wipe): before dispatching, this interceptor re-runs
 *    {@link AdminAuthService.resolveAdminIdentity}, the same identity/tenant
 *    lookup the app performs at login, which re-populates the key on success.
 *    If it still can't resolve a tenant (not an admin, or the lookup itself
 *    fails), the request proceeds header-less — server-side 403 +
 *    `httpErrorInterceptor`'s generic "You do not have access to that
 *    resource." toast is the documented fallback, not a bug.
 *
 *    This is NOT as clean as it sounds: {@link AdminAuthService.resolveAdminIdentity}
 *    itself issues an HTTP request (the admin-me identity lookup),
 *    which re-enters this same global interceptor while the key is still
 *    missing. Two guards keep that from recursing:
 *      1. A request to the identity-resolution endpoint itself is never
 *         tenant-scoped, so it is dispatched header-less immediately,
 *         without ever calling `resolveAdminIdentity` on its own behalf.
 *      2. A `resolving` flag ensures only one `resolveAdminIdentity` call is
 *         ever in flight per interceptor instance — any other missing-key
 *         request that shows up while it's running is sent header-less
 *         rather than starting a second (recursive) resolution.
 *  - **Key present but wrong** (stale/foreign tenant id, hand-edited
 *    storage): the client has no way to tell a wrong value from a right one,
 *    so it is attached as-is — no re-fetch is triggered. The server's
 *    membership check rejects it and the caller sees the same 403 toast as
 *    the missing-key fallback case.
 */
@Injectable()
export class TenantInterceptor implements HttpInterceptor {
  private adminAuthService = inject(AdminAuthService);

  // Guards the recursion described above (#2499): only one resolveAdminIdentity()
  // call may be in flight per interceptor instance at a time.
  private resolving = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const tenantId = localStorage.getItem('tenantId');

    if (tenantId) {
      return next.handle(this.withTenantHeader(request, tenantId));
    }

    // The identity-resolution endpoint is not tenant-scoped. Re-fetching
    // identity for a request TO that endpoint would just call
    // resolveAdminIdentity() to decide how to send a request that IS
    // resolveAdminIdentity()'s own request — send it header-less as-is.
    if (this.isIdentityResolutionRequest(request)) {
      return next.handle(request);
    }

    // A resolveAdminIdentity() call is already in flight (most likely its own
    // nested identity-lookup request re-entering this interceptor). Don't
    // start a second one — proceed header-less; the in-flight resolution
    // only benefits requests made after it settles.
    if (this.resolving) {
      return next.handle(request);
    }

    this.resolving = true;
    return this.adminAuthService.resolveAdminIdentity().pipe(
      switchMap(() => {
        const resolvedTenantId = localStorage.getItem('tenantId');
        const outgoing = resolvedTenantId ? this.withTenantHeader(request, resolvedTenantId) : request;
        return next.handle(outgoing);
      }),
      finalize(() => {
        this.resolving = false;
      }),
    );
  }

  // Matches the admin-me tenant-resolution route built in
  // AdminAuthService.getAdminUserDetails (`${env.api}/tenant-api/admin-me`).
  private isIdentityResolutionRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes('admin-me');
  }

  private withTenantHeader(request: HttpRequest<unknown>, tenantId: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        'X-Tenant-ID': tenantId,
      },
    });
  }
}
